import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
    getPublicArticleById,
    resetPublicArticleDetail,
} from "../../../../store/features/getPublicArticleById";
import { createExternalKbFeedback } from "../../../../store/features/createExternalKbFeedback";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import ArticleContent from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/ArticleContent";
import CommentsSection from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/CommentsSection";
import RelatedArticles from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/RelatedArticles";
import ArticleCategory from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/ArticleCategory";
import ArticleError from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/ArticleError";
import ArticleSkeleton from "../../../../components/dashboardcomponents/KnowledgeBase/PublicArticleView/ArticleSkeleton";
import { getKbGroupId } from "../../../../utils/kbGroupId";

const PublicArticleView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { articleId, businessId } = useParams();
    const { data: kbSettings } = useSelector((state) => state?.externalKbSettings);
    const accentColor = kbSettings?.data?.accent_color;

    const { article, loading, error } = useSelector(
        (state) => state.publicArticleDetail
    );
    const articleMatchesRoute =
        Boolean(article) &&
        Boolean(articleId) &&
        (String(article._id) === String(articleId) ||
            (article.slug && String(article.slug) === String(articleId)));
    const business_id = useSelector(
        (state) => state.loginToken?.response?.user?.business_id
    );
    const { loading: feedbackLoading } = useSelector(
        (state) => state.createExternalKbFeedback
    );

    const [isNotPublic, setIsNotPublic] = useState(false);
    const [comment, setComment] = useState("");
    const [comments, setComments] = useState([]);

    useLayoutEffect(() => {
        setIsNotPublic(false);
        setComments([]);

        if (!articleId) return undefined;

        dispatch(getPublicArticleById({ articleId, group_id: getKbGroupId() }));

        return () => {
            dispatch(resetPublicArticleDetail());
        };
    }, [dispatch, articleId]);

    useEffect(() => {
        if (article) {
            if (article.visibility?.toLowerCase() !== "public") {
                setIsNotPublic(true);
            }

            const parsedComments = article.feedbacks
                ? article.feedbacks
                    .filter((feedback) => feedback.source === "visitor")
                    .flatMap((feedback) =>
                        (feedback.comments || []).map((c) => ({
                            id: c._id,
                            author: feedback.name || "Visitor",
                            email: feedback.email || "",
                            avatar: "",
                            date: new Date(c.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                            }),
                            content: c.comment,
                        }))
                    )
                : [];
            setComments(parsedComments);
        }
    }, [article]);

    const handleSubmitComment = async (formData) => {
        const { name, email, comment: commentText } = formData;

        if (!commentText?.trim()) {
            toast.warning("Please enter a comment");
            return;
        }

        const payload = {
            business_id: businessId || business_id,
            articleId: articleId,
            comment: commentText.trim(),
            source: "visitor",
            name: name?.trim() || "",
            email: email?.trim() || "",
            group_id: getKbGroupId(),
        };

        try {
            await dispatch(createExternalKbFeedback(payload)).unwrap();
            toast.success("Comment submitted successfully!");

            const newComment = {
                id: Date.now().toString(),
                author: name?.trim() || "Visitor",
                email: email?.trim() || "",
                avatar: "",
                date: new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                }),
                content: commentText.trim(),
            };
            setComments((prev) => [...prev, newComment]);
            setComment("");

        } catch (error) {
            toast.error(error?.message || "Failed to submit comment");
        }
    };

    if (loading && !articleMatchesRoute) {
        return <ArticleSkeleton />;
    }

    if (error || isNotPublic) {
        return <ArticleError isNotPublic={isNotPublic} navigate={navigate} />;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                width: "100%",
                backgroundColor: "#fff",
                alignItems: "center",
                marginTop: { xs: "60px", sm: "80px", md: "100px", lg: "120px" },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: { xs: "24px", sm: "28px", md: "32px", lg: "40px" },
                    maxWidth: "1440px",
                    width: "100%",
                    justifyContent: "center",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ArticleContent article={article} />

                    {/* Only show comments section if comments are not disabled */}
                    {!article?.disableComments && (
                        <CommentsSection
                            comment={comment}
                            setComment={setComment}

                            handleSubmitComment={handleSubmitComment}
                            feedbackLoading={feedbackLoading}
                            comments={comments}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        width: { lg: "290px", xs: "100%" },
                        maxWidth: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        alignSelf: { lg: "flex-start" },
                    }}
                >
                    <RelatedArticles articles={article?.relatedArticles || []} />
                    <ArticleCategory article={article} accentColor={accentColor} />
                </Box>
            </Box>
        </Box>
    );
};

export default PublicArticleView;
